import type { ReactNode } from 'react'
import type { IndexDetail } from '@/lib/market/builders/indices'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { ChangeText } from '@/components/market/change'
import { useIndexQuote, useQuotes } from '@/components/market/live-provider'
import { formatNumber, formatPercent, formatPrice, formatSigned } from '@/lib/market/format'

interface IndexConstituentsProps {
    index: IndexDetail
}

const IndexConstituents = ({ index }: IndexConstituentsProps): ReactNode => {
    const [expanded, setExpanded] = useState(false)
    const specs = useMemo(() => index.constituents.map(item => item.live), [index.constituents])
    const quotes = useQuotes(specs)
    const indexQuote = useIndexQuote(index.live)

    const rows = useMemo(() => {
        const count = Math.max(1, index.constituents.length)
        let currentTotal = 0
        let previousTotal = 0

        const raw = index.constituents.map((item, position) => {
            const quote = quotes[position]
            let current = 0
            let previous = 0

            if (index.code === 'KASI') {
                current = quote.last * item.shares
                previous = quote.prevClose * item.shares
            }
            else if (index.code === 'K25') {
                current = item.weight * (quote.last / item.reference)
                previous = item.weight * (quote.prevClose / item.reference)
            }
            else {
                current = 1
                previous = 1
            }

            currentTotal += current
            previousTotal += previous

            return { item, quote, current, previous }
        })

        return raw.map(row => {
            const move = row.quote.prevClose > 0 ? (row.quote.last - row.quote.prevClose) / row.quote.prevClose : 0
            let points = 0

            if (index.code === 'KASI') points = previousTotal > 0 ? indexQuote.prevClose * (row.previous / previousTotal) * move : 0
            else if (index.code === 'K25') points = index.target * row.item.weight * ((row.quote.last - row.quote.prevClose) / row.item.reference)
            else points = (indexQuote.prevClose * move) / count

            return {
                ...row,
                weight: currentTotal > 0 ? (row.current / currentTotal) * 100 : 0,
                points
            }
        }).sort((a, b) => b.weight - a.weight)
    }, [index, quotes, indexQuote.prevClose])

    const visible = expanded ? rows : rows.slice(0, 12)
    const maxWeight = Math.max(1, ...rows.map(row => row.weight))
    const totalPoints = rows.reduce((acc, row) => acc + row.points, 0)

    return (
        <div>
            <div className='-mx-4 overflow-x-auto px-4 scroll-thin sm:mx-0 sm:px-0'>
                <table className='w-full min-w-[560px] text-left text-[13px]'>
                    <caption className='sr-only'>{index.name} constituents with weights and contribution to the index move today</caption>
                    <thead>
                        <tr className='border-b border-border text-[11px] uppercase tracking-[0.06em] text-muted-foreground'>
                            <th scope='col' className='py-2 pr-3 font-medium'>Constituent</th>
                            <th scope='col' className='py-2 pr-3 text-right font-medium'>Last</th>
                            <th scope='col' className='py-2 pr-3 text-right font-medium'>Today</th>
                            <th scope='col' className='py-2 pr-3 font-medium'>Weight</th>
                            <th scope='col' className='py-2 text-right font-medium' title='Index points added or removed today'>Points</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map(row => (
                            <tr key={row.item.id} className='border-b border-border/60 last:border-0'>
                                <td className='max-w-52 py-2 pr-3'>
                                    <Link href={`/stocks/${row.item.slug}`} className='group block'>
                                        <span className='font-mono text-[13px] font-semibold text-foreground group-hover:text-primary'>{row.item.ticker}</span>
                                        <span className='block truncate text-xs text-muted-foreground'>
                                            <span data-mdlfy-cms={`companies:${row.item.id}:name`}>{row.item.name}</span>
                                        </span>
                                    </Link>
                                </td>
                                <td className='py-2 pr-3 text-right tabular-nums text-foreground'>{formatPrice(row.quote.last)}</td>
                                <td className='py-2 pr-3 text-right'><ChangeText value={row.quote.changePct} /></td>
                                <td className='py-2 pr-3'>
                                    <div className='flex items-center gap-2'>
                                        <span className='relative h-1.5 w-20 rounded-full bg-secondary' aria-hidden='true'>
                                            <span className='absolute inset-y-0 left-0 rounded-full bg-series-1' style={{ width: `${(row.weight / maxWeight) * 100}%` }} />
                                        </span>
                                        <span className='tabular-nums text-foreground'>{formatPercent(row.weight, 2, false)}</span>
                                    </div>
                                </td>
                                <td className={cn('py-2 text-right tabular-nums', row.points > 0 ? 'text-up' : row.points < 0 ? 'text-down' : 'text-muted-foreground')}>
                                    {formatSigned(row.points, 2)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className='mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground'>
                <p>
                    {rows.length} constituents. Contributions sum to <span className='tabular-nums text-foreground'>{formatSigned(totalPoints, 2)}</span> points
                    {index.code === 'K20' ? ', an arithmetic approximation of the geometric index.' : '.'}
                </p>
                {rows.length > 12 && (
                    <button type='button' onClick={() => setExpanded(value => !value)} className='font-medium text-primary hover:underline'>
                        {expanded ? 'Show top 12' : `Show all ${rows.length}`}
                    </button>
                )}
            </div>

            <p className='sr-only'>Index level {formatNumber(indexQuote.level, 2)}</p>
        </div>
    )
}

export default IndexConstituents
